import { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import {
    DocumentNodeType,
    DocumentOverviewItemDto,
    DocumentType,
    FolderType,
    IDefaultFolderDto,
    IDocumentOverviewItemDto,
    IDocumentStackItemDto,
    IFolderOverviewItemDto,
    IOverviewItemDto,
    IRootOverviewItemDto,
    isFolder,
} from '../config/documentConfig'

// Re-exported from documentConfig to preserve existing import paths.
// The enum definitions live in documentConfig to avoid a circular dependency
// (documentConfig uses DocumentType at module-load time to build its Maps).
// export { DocumentNodeType, DocumentType } from '../config/documentConfig'
import { DriveItem, DriveItemUploadableProperties, UploadSession } from '@microsoft/microsoft-graph-types'
import { Destination } from '../context/AxiosContext'
import { DocumentOpenTarget, DocumentOpenType, DocumentsOpen } from '../context/GlobalContext'
import { Dispatch } from '../reducers'
import { ErrorAction, ErrorType } from '../reducers/errorReducer'
import { findDefaultFolderPaths } from './folderSettingsHelper'
import { formatInputDocumentName } from './regexHelper'

// These are the document properties that are provided by SharePoint.
export const driveItemOverviewProperties: string[] = [
    'id',
    'name',
    'webUrl',
    'webDavUrl',
    'parentReference',
    'size',
    'folder',
    'file',
    'lastModifiedDateTime',
]
export const driveItemListProperties: string[] = ['id', 'name', 'parentReference', 'folder', 'webUrl']

// Used for discovering the application an extension belongs to.
export const DocumentTypeMap: Map<string, DocumentType> = new Map([
    ['accdb', DocumentType.Access],
    ['ods', DocumentType.Excel],
    ['xls', DocumentType.Excel],
    ['xlsb', DocumentType.Excel],
    ['xlsm', DocumentType.Excel],
    ['xlsx', DocumentType.Excel],
    ['csv', DocumentType.ExcelCsv],
    ['xlt', DocumentType.ExcelTpl],
    ['xltm', DocumentType.ExcelTpl],
    ['xltx', DocumentType.ExcelTpl],
    ['xsn', DocumentType.Infopath],
    ['one', DocumentType.OneNote],
    ['onetoc', DocumentType.OneNoteToc],
    ['eml', DocumentType.Outlook],
    ['msg', DocumentType.Outlook],
    ['odp', DocumentType.PowerPoint],
    ['ppt', DocumentType.PowerPoint],
    ['pptm', DocumentType.PowerPoint],
    ['pptx', DocumentType.PowerPoint],
    ['pps', DocumentType.PowerPointSs],
    ['ppsm', DocumentType.PowerPointSs],
    ['ppsx', DocumentType.PowerPointSs],
    ['pot', DocumentType.PowerPointTpl],
    ['potm', DocumentType.PowerPointTpl],
    ['potx', DocumentType.PowerPointTpl],
    ['mpp', DocumentType.Project],
    ['mpt', DocumentType.ProjectTpl],
    ['pub', DocumentType.Publisher],
    ['pdf', DocumentType.Reader],
    ['vsd', DocumentType.Visio],
    ['vsdm', DocumentType.Visio],
    ['vsdx', DocumentType.Visio],
    ['vss', DocumentType.VisioStencil],
    ['vssm', DocumentType.VisioStencil],
    ['vssx', DocumentType.VisioStencil],
    ['vst', DocumentType.VisioTpl],
    ['vstm', DocumentType.VisioTpl],
    ['vstx', DocumentType.VisioTpl],
    ['doc', DocumentType.Word],
    ['docm', DocumentType.Word],
    ['docx', DocumentType.Word],
    ['odt', DocumentType.Word],
    ['rtf', DocumentType.Word],
    ['wps', DocumentType.Word],
    ['wbk', DocumentType.Word],
    ['dot', DocumentType.WordTpl],
    ['dotm', DocumentType.WordTpl],
    ['dotx', DocumentType.WordTpl],
])

export const OfficeUriMap: Map<DocumentType, string> = new Map([
    [DocumentType.Access, 'ms-access:ofe'],
    [DocumentType.Excel, 'ms-excel:ofe'],
    [DocumentType.ExcelCsv, 'ms-excel:ofe'],
    [DocumentType.ExcelTpl, 'ms-excel:nft'],
    [DocumentType.Infopath, 'ms-infopath:ofe'],
    [DocumentType.PowerPoint, 'ms-powerpoint:ofe'],
    [DocumentType.PowerPointSs, 'ms-powerpoint:ofe'],
    [DocumentType.PowerPointTpl, 'ms-powerpoint:nft'],
    [DocumentType.Project, 'ms-project:ofe'],
    [DocumentType.ProjectTpl, 'ms-project:nft'],
    [DocumentType.Publisher, 'ms-publisher:ofe'],
    [DocumentType.Visio, 'ms-visio:ofe'],
    [DocumentType.VisioStencil, 'ms-visio:ofe'],
    [DocumentType.VisioTpl, 'ms-visio:nft'],
    [DocumentType.Word, 'ms-word:ofe'],
    [DocumentType.WordTpl, 'ms-word:nft'],
])

export const previewFileTypes = [
    'jpg',
    'jpeg',
    'gif',
    'png',
    'eps',
    'tif',
    'tiff',
    'bmp',
    'svg',
    'mp4',
    'mov',
    'wmv',
    'avi',
    'avchd',
    'mkv',
    'webm',
    'mpg',
    'mpeg',
    'gifv',
    'ogg',
    'mpv',
    'htm',
    'html',
    'pdf',
    'ods',
    'xls',
    'xlsb',
    'xlsm',
    'xlsx',
    'csv',
    'xlt',
    'xltm',
    'xltx',
    'xsn',
    'one',
    'onetoc',
    'eml',
    'msg',
    'odp',
    'ppt',
    'pptm',
    'pptx',
    'pps',
    'ppsm',
    'ppsx',
    'pot',
    'potm',
    'potx',
    'mpp',
    'mpt',
    'pub',
    'pdf',
    'vsd',
    'vsdm',
    'vsdx',
    'vss',
    'vssm',
    'vssx',
    'vst',
    'vstm',
    'vstx',
    'doc',
    'docm',
    'docx',
    'odt',
    'rtf',
    'wps',
    'dot',
    'dotm',
    'dotx',
]

// File types that we can convert to PDF
// https://docs.microsoft.com/en-us/graph/api/driveitem-get-content-format?view=graph-rest-1.0&tabs=http#format-options
export const convertableDocumentTypes: string[] = [
    'doc',
    'docx',
    'dot',
    'dotx',
    'dotm',
    'dsn',
    'dwg',
    'eml',
    'epub',
    'fluidframework',
    'form',
    'htm',
    'html',
    'loop',
    'loot',
    'markdown',
    'md',
    'msg',
    'note',
    'odp',
    'ods',
    'odt',
    'page',
    'pps',
    'ppsx',
    'ppt',
    'pptx',
    'pulse',
    'rtf',
    'task',
    'tif',
    'tiff',
    'wbtx',
    'whiteboard',
    'xls',
    'xlsm',
    'xlsx',
]

/**
 * Remove the first digits from a folder before displaying. These digits are added to ensure the correct sorting.
 *
 * @param filename
 */
export function formatFolderName(filename: string): string {
    // Remove first 2 digits, if any.
    return filename ? filename.replace(/^(\d{2}\s)(.*)/, '$2') : ''
}

export const formatDocumentNodeName = (name: string, type: DocumentNodeType): string =>
    type === DocumentNodeType.Folder ? formatFolderName(name) : name

export function getDocumentNameFromPath(path?: string) {
    return /(.+?)(\.[^.]*$|$)/.exec(path || '')[1]
}

export function mapDriveItemToOverviewItemDto(item: DriveItem, stack: IDocumentStackItemDto[] = []): DocumentOverviewItemDto {
    const baseItem: IOverviewItemDto = {
        id: item.id,
        name: item.name,
        type: getDocumentNodeType(item),
        parentId: item.parentReference?.id,
        url: formatDocumentUrl(item, getOpenUrl),
        webDavUrl: item.webDavUrl,
        webUrl: item.webUrl,
        stack,
    }
    switch (baseItem.type) {
        case DocumentNodeType.Folder:
            return {
                ...baseItem,
                // The children will be further traversed in reduceChildDocuments().
                children: item.children?.map(child => mapDriveItemToOverviewItemDto(child, [...stack, { id: item.id, name: item.name }])),
            } as IFolderOverviewItemDto
        case DocumentNodeType.Document:
        default:
            return {
                ...baseItem,
                date: item.lastModifiedDateTime,
            } as IDocumentOverviewItemDto
    }
}

const matchFolderChildByLabel = (parent: IFolderOverviewItemDto, label: string): IFolderOverviewItemDto => {
    const search: string = label.toLowerCase()
    return (parent.children || []).filter(isFolder).find(folder => folder.name.toLowerCase().indexOf(search) !== -1) as IFolderOverviewItemDto
}

export const findFolderInTree = (
    tree: IRootOverviewItemDto,
    folderType: FolderType | string,
    defaultFolders: IDefaultFolderDto[],
): IFolderOverviewItemDto => {
    if (!tree) {
        return undefined
    }

    // Walk each candidate chain (enabled chains first) level by level through the loaded tree;
    // the first chain that fully exists wins, so old dossiers keep resolving to their disabled
    // (old) folder.
    for (const path of findDefaultFolderPaths(defaultFolders, folderType)) {
        let current: IFolderOverviewItemDto = tree
        for (const segment of path) {
            current = matchFolderChildByLabel(current, segment.label)
            if (!current) {
                break
            }
        }
        if (current) {
            return current
        }
    }
    return undefined
}

/**
 * findFolderInTree for trees that may not have every level loaded: intermediate folders without
 * loaded children are fetched (one level at a time) before matching the next segment.
 */
export async function resolveFolderInTree(
    axios: AxiosInstance,
    tree: IRootOverviewItemDto,
    folderType: FolderType | string,
    defaultFolders: IDefaultFolderDto[],
): Promise<IFolderOverviewItemDto> {
    if (!tree) {
        return undefined
    }

    for (const path of findDefaultFolderPaths(defaultFolders, folderType)) {
        let current: IFolderOverviewItemDto = tree
        for (const segment of path) {
            if (!current.children) {
                current = (await axios
                    .get<DriveItem>(`/items/${current.id}`, {
                        baseURL: Destination.Documents,
                        params: { $select: driveItemListProperties.join(','), $expand: 'children' },
                        headers: { SuppressErrors: 'true' },
                    })
                    .then(response => mapDriveItemToOverviewItemDto(response.data, current.stack))) as IFolderOverviewItemDto
            }
            current = matchFolderChildByLabel(current, segment.label)
            if (!current) {
                break
            }
        }
        if (current) {
            return current
        }
    }
    return undefined
}

export async function checkDocument(id: string, axios: AxiosInstance, attempt: number = 1): Promise<AxiosResponse<DriveItem>> {
    return axios
        .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${id}`, {
            baseURL: Destination.Documents,
            headers: { SuppressErrors: attempt <= 5 ? 'true' : undefined },
            params: { $select: 'id' },
        })
        .catch(async (error: AxiosError) => {
            if (attempt <= 5) {
                await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt)))
                return checkDocument(id, axios, ++attempt)
            }
            return Promise.reject(error)
        })
}

export const uploadDocument = async (axios: AxiosInstance, dispatch: Dispatch, folderId: string, document: File): Promise<any> => {
    // Max file size appears to be 256MB (= 2^28).
    if (document.size > 2 ** 28) {
        const size: string = (document.size / 2 ** 20).toFixed(2)
        return dispatch({
            type: ErrorAction.PushError,
            payload: {
                id: document.name,
                type: ErrorType.Graph,
                message: `Het bestand '${document.name}' overschrijdt de limiet van 256 MB (${size} MB).`,
            },
        })
    }

    const documentName = formatInputDocumentName(document.name)

    // Implementation of this procedure:
    // https://docs.microsoft.com/en-us/graph/api/driveitem-createuploadsession
    const session = await axios.post<UploadSession>(
        `/items/${folderId}:/${encodeURIComponent(documentName)}:/createUploadSession`,
        {
            item: {
                '@microsoft.graph.conflictBehavior': 'rename',
                name: documentName,
            } as DriveItemUploadableProperties,
        },
        { baseURL: Destination.Documents },
    )

    return axios.put<DriveItem>(session.data.uploadUrl, document, {
        headers: {
            'Content-Range': `bytes 0-${document.size - 1}/${document.size}`,
        },
    })
}

export async function convertDroppedItemToFile(item: Office.DroppedItemDetails): Promise<File> {
    const bytes = (await item.fileContent.stream().getReader().read()).value
    const type = item.type === 'eml' ? 'message/rfc822' : item.type === 'msg' ? 'application/vnd.ms-outlook' : 'application/octet-stream'

    // Most modern browsers support File; if not, fall back to Blob
    try {
        return new File([bytes], item.name, { type, lastModified: Date.now() })
    } catch {
        const blob: any = new Blob([bytes], { type })
        blob.name = item.name // Safari < 14 fallback
        return blob as File
    }
}

export function getDocumentExtension(filename: string): string {
    const regex = /(?:\.([^\\/.]+))?$/.exec(filename || '')
    return regex[1] ? regex[1].toLowerCase() : null
}

export interface ExtensionChange {
    oldExtension: string
    newExtension: string
    isRemoved: boolean
    isChanged: boolean
}

// Compares extensions case-insensitively: renaming contract.PDF to contract.pdf is not a change (HAM-4101).
export function getExtensionChange(oldName: string, newName: string): ExtensionChange {
    const oldExtension = getDocumentExtension(oldName)
    const newExtension = getDocumentExtension(newName)
    return {
        oldExtension,
        newExtension,
        isRemoved: !newExtension,
        isChanged: !!oldExtension && !!newExtension && oldExtension !== newExtension,
    }
}

export const isConvertableToPdf = (filename: string): boolean => convertableDocumentTypes.includes(getDocumentExtension(filename))

export function getDocumentType(filename?: string): DocumentType {
    const extension = getDocumentExtension(filename)
    return (extension && DocumentTypeMap.get(extension)) || DocumentType.Other
}

export function getDocumentNodeType(item: DriveItem): DocumentNodeType {
    return item.hasOwnProperty('folder') ? DocumentNodeType.Folder : DocumentNodeType.Document
}

const getDriveItemWithProps = (id: string, props: string[], axios: AxiosInstance): Promise<DriveItem> =>
    axios
        .get<DriveItem>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: props.join(',') },
        })
        .then(result => result.data)

export const getDriveItemDownload = (id: string, axios: AxiosInstance): Promise<DriveItem> =>
    getDriveItemWithProps(id, ['id', 'name', 'content.downloadUrl', 'webUrl', 'webDavUrl'], axios)

export const getDriveItem = (id: string, axios: AxiosInstance): Promise<DriveItem> => getDriveItemWithProps(id, driveItemOverviewProperties, axios)

export const getOpenUrl = (driveItem: DriveItem): string => driveItem.webDavUrl || driveItem.webUrl

export const getDownloadUrl = (driveItem: DriveItem): string => driveItem['@microsoft.graph.downloadUrl'] || driveItem['@content.downloadUrl']

export const getDriveItemDownloadUrl = (id: string, axios: AxiosInstance): Promise<string> =>
    getDriveItemWithProps(id, ['content.downloadUrl'], axios).then(getDownloadUrl)

// Moves drive items to another folder. Shared by the Move dialog (conflictBehavior 'rename')
// and drag & drop on the documents tree, which uses 'fail' so a same-name conflict surfaces
// as a Graph 409 nameAlreadyExists instead of silently renaming (HAM-3993).
export const moveDriveItems = (
    axios: AxiosInstance,
    driveId: string,
    items: DriveItem[],
    targetFolderId: string,
    conflictBehavior: 'rename' | 'fail' | 'replace' = 'rename',
): Promise<DriveItem[]> =>
    Promise.all(
        items.map(item =>
            axios
                .patch<DriveItem, AxiosResponse<DriveItem>>(
                    `/items/${item.id}`,
                    { parentReference: { driveId, id: targetFolderId } },
                    {
                        baseURL: Destination.Documents,
                        params: { '@microsoft.graph.conflictBehavior': conflictBehavior },
                        headers: { SuppressErrors: 'true' },
                    },
                )
                .then(result => result.data),
        ),
    )

export function formatDocumentUrl(driveItem?: DriveItem, getUrl?: (driveItem: DriveItem) => string): string {
    if (!driveItem) {
        return undefined
    }

    if (getDocumentNodeType(driveItem) === DocumentNodeType.Document) {
        const documentType: DocumentType = getDocumentType(driveItem?.name)

        // Not all document types map to an Office URI.
        if (OfficeUriMap.has(documentType)) {
            return driveItem?.webDavUrl ? `${OfficeUriMap.get(documentType)}|u|${driveItem?.webDavUrl}` : driveItem?.webUrl
        }
    }

    return getUrl?.(driveItem) || getDownloadUrl(driveItem)
}

// The default DOCUMENTS_OPEN behavior when the setting is absent: open (not download) in the
// desktop app (protocol handler). Preserves the pre-HAM-4251 behavior.
export const DEFAULT_DOCUMENTS_OPEN: DocumentsOpen = { action: DocumentOpenType.Open, target: DocumentOpenTarget.App }

// True when clicking should open the document rather than download it. An Office protocol url
// (ms-word:...) always opens; otherwise it depends on the action being OPEN.
export const shouldOpen = (documentsOpen: DocumentsOpen | undefined, isOfficeUrl: boolean): boolean =>
    (documentsOpen?.action ?? DEFAULT_DOCUMENTS_OPEN.action) !== DocumentOpenType.Download || isOfficeUrl

// The url to open a document with, honoring the target: ONLINE opens the Office Online web url
// (driveItem.webUrl), otherwise the desktop app protocol handler built by formatDocumentUrl.
export const resolveOpenUrl = (driveItem: DriveItem | undefined, documentsOpen?: DocumentsOpen): string =>
    documentsOpen?.target === DocumentOpenTarget.Online ? driveItem?.webUrl : formatDocumentUrl(driveItem, getOpenUrl)
